/**
 * The annotation data model (unchanged on the server): a point note has
 * `time`; a range note also has `endTime` > `time`.
 */
export interface StudioAnnotation {
  id: string;
  time: number;
  text: string;
  category?: string;
  color?: string;
  endTime?: number;
}

export const isRangeAnnotation = (annotation: { time: number; endTime?: number | null }) =>
  typeof annotation.endTime === 'number' &&
  Number.isFinite(annotation.endTime) &&
  annotation.endTime > annotation.time;

/**
 * Notes that apply at `time`: a range containing it, or a point within
 * `windowSeconds` after it (a note reads as "now" for a moment after its frame).
 */
export const annotationsActiveAt = <T extends { id: string; time: number; endTime?: number | null }>(
  annotations: T[],
  time: number,
  windowSeconds = 1.5
): T[] =>
  annotations.filter((annotation) =>
    isRangeAnnotation(annotation)
      ? time >= annotation.time && time <= (annotation.endTime as number)
      : time >= annotation.time - 0.05 && time - annotation.time <= windowSeconds
  );

/** Unique, collision-safe id for a new note (two in one millisecond happen). */
export const newAnnotationId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const sortByTime = <T extends { time: number }>(annotations: T[]): T[] =>
  [...annotations].sort((a, b) => a.time - b.time);

/** Previous/next note start for `[` and `]`. */
export const adjacentAnnotationTime = (
  annotations: Array<{ time: number }>,
  time: number,
  direction: 1 | -1
): number | null => {
  const starts = Array.from(new Set(annotations.map((a) => a.time))).sort((a, b) => a - b);
  if (direction === 1) return starts.find((t) => t > time + 0.05) ?? null;
  // Within a second after a note, "previous" means the one before it.
  const earlier = starts.filter((t) => t < time - 0.75);
  return earlier.length ? earlier[earlier.length - 1] : null;
};

/**
 * Where a new note lands given the In/Out marks and the playhead: both marks
 * make a range; one mark makes a range to the playhead (either direction);
 * no marks, or a span under one frame, make a point note at the playhead.
 */
export const resolveNoteSpan = (
  inPoint: number | null,
  outPoint: number | null,
  playhead: number,
  minSpan = 1 / 30
): { time: number; endTime?: number } => {
  let a: number | null = null;
  let b: number | null = null;
  if (inPoint !== null && outPoint !== null) {
    a = inPoint;
    b = outPoint;
  } else if (inPoint !== null) {
    a = inPoint;
    b = playhead;
  } else if (outPoint !== null) {
    a = playhead;
    b = outPoint;
  }
  if (a === null || b === null) return { time: Math.max(0, playhead) };
  const start = Math.max(0, Math.min(a, b));
  const end = Math.max(a, b);
  if (end - start < minSpan) return { time: start };
  return { time: start, endTime: end };
};
